import React, {useEffect, useState} from 'react';
import {Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions} from '@headlessui/react';
import Icon from '@/shared/components/Icon';
import {ProcessedBiomarker, cleanBiomarkerName} from '@/shared/utils/biomarkerUtils';

interface BiomarkerSelectProps {
    biomarkers: ProcessedBiomarker[];
    selectedBiomarker: ProcessedBiomarker | null;
    onSelect: (biomarker: ProcessedBiomarker) => void;
}

const BiomarkerSelect: React.FC<BiomarkerSelectProps> = ({biomarkers, selectedBiomarker, onSelect}) => {
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query.trim().toLowerCase()), 300);
        return () => clearTimeout(timer);
    }, [query]);

    if (biomarkers.length === 0) return null;

    const filteredBiomarkers = debouncedQuery
        ? biomarkers.filter((b) => cleanBiomarkerName(b.testName).toLowerCase().includes(debouncedQuery))
        : biomarkers;

    return (
        <Combobox
            value={selectedBiomarker}
            onChange={(b: ProcessedBiomarker | null) => {
                if (b) {
                    onSelect(b);
                    setQuery('');
                }
            }}>
            <div className="relative w-full sm:w-auto sm:min-w-[280px] mb-2">
                <div className="relative flex items-center text-sm border border-mld-panel-border bg-mld-menu-background rounded-lg focus-within:border-mld-primary">
                    <Icon name="magnifying-glass" className="w-4 h-4 ml-3 flex-shrink-0 text-mld-muted-text" />
                    <ComboboxInput
                        className="flex-1 min-w-0 border-0 bg-transparent outline-none focus:ring-0 px-2 py-1 text-mld-menu-text placeholder:text-mld-muted-text truncate"
                        placeholder="Search Biomarker"
                        displayValue={(b: ProcessedBiomarker | null) => (b ? cleanBiomarkerName(b.testName) : '')}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    <ComboboxButton className="flex items-center justify-center pr-3 pl-1 py-2">
                        <Icon name="chevron-down" className="w-4 h-4 flex-shrink-0 text-mld-muted-text" />
                    </ComboboxButton>
                </div>

                <ComboboxOptions className="absolute right-0 left-0 mt-1 w-full bg-mld-menu-background rounded-lg border border-mld-panel-border max-h-60 overflow-auto focus:outline-none z-[100] shadow-md">
                    {filteredBiomarkers.length === 0 ? (
                        <div className="px-3 sm:px-4 py-3 text-sm text-mld-muted-text">No biomarkers found</div>
                    ) : (
                        filteredBiomarkers.map((biomarker) => (
                            <ComboboxOption
                                key={biomarker.testName}
                                value={biomarker}
                                className="cursor-pointer px-3 sm:px-4 py-3 text-sm text-mld-menu-text data-[focus]:bg-mld-primary/10 data-[selected]:font-medium">
                                {cleanBiomarkerName(biomarker.testName)}
                            </ComboboxOption>
                        ))
                    )}
                </ComboboxOptions>
            </div>
        </Combobox>
    );
};

export default BiomarkerSelect;
